import { Loading } from '@carbon/react';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import styles from './AllEntities.module.scss';

import { AllEntitiesProvider } from 'Screens/Dashboard/AllEntities/AllEntitiesProvider/AllEntitiesProvider';
import AddEntity from 'Screens/Dashboard/AllEntities/AddEntity/AddEntity';
import { AddEntityProvider } from 'Screens/Dashboard/AllEntities/EntityProvider/AddEntityProvider';
import EditEntityContainer from 'Screens/Dashboard/AllEntities/EditEntityContainer/EditEntityContainer';
import { useGetEntitiesQuery } from 'api/entityEndpoints';
import EntityTable from 'Screens/Dashboard/AllEntities/EntityTable/EntityTable';
import ErrorActionState from 'Components/Common/ErrorState/ErrorActionState';
import { useMainContainer } from 'Components/Common/Container/context/ContainerContext';
import { Add } from '@carbon/icons-react';
import useGetUser from 'hooks/useGetUser';
import cx from 'classnames';
import NotConfiguredEmptyState from 'Components/Common/NotConfiguredEmptyState/NotConfiguredEmptyState';
import EntitiesInfoCard from './EntitiesInfoCard/EntitiesInfoCard';

const AllEntities = () => {
    const intl = useIntl();
    const [createEntityOpen, setCreateEntityOpen] = useState(false);
    const {
        data, isFetching, isError, refetch,
    } = useGetEntitiesQuery();
    const {
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
        setDisabledAction,
        setDisabledActionTooltip,
    } = useMainContainer();
    const isEmpty = data?.length === 0;

    useEffect(() => {
        setTopBarAction(() => () => {
            setCreateEntityOpen(true);
        });
        setTopBarActionTitle(intl.formatMessage(messages.addNew));
        setTopBarIcon(<Add size={20} />);
        setDisabledAction(false);
        setDisabledActionTooltip(undefined);

        return () => {
            setTopBarAction(null);
            setTopBarActionTitle(undefined);
            setTopBarIcon(undefined);
            setDisabledAction(false);
            setDisabledActionTooltip(undefined);
        };
    }, [
        intl,
        setDisabledAction,
        setDisabledActionTooltip,
        setTopBarAction,
        setTopBarActionTitle,
        setTopBarIcon,
    ]);

    const onCreateEntityClose = () => {
        setCreateEntityOpen(false);
    };

    return (
        <AllEntitiesProvider>
            <div className={styles.outerContainer}>
                <div className={cx(styles.container, isEmpty && styles.emptyContainer)}>
                    {!isEmpty && <EntitiesInfoCard />}
                    {!isError && <EntityTable />}

                    <AddEntityProvider key={createEntityOpen ? 'create-open' : 'create-closed'}>
                        <AddEntity open={createEntityOpen} onClose={onCreateEntityClose} />
                    </AddEntityProvider>
                    {(() => {
                        if (isFetching) {
                            return <Loading />;
                        }

                        if (isError) {
                            return (
                                <div className={styles.error}>
                                    <ErrorActionState onClick={refetch} />
                                </div>
                            );
                        }

                        if (data) {
                            return <EditEntityContainer />;
                        }

                        return null;
                    })()}
                </div>
            </div>

        </AllEntitiesProvider>
    );
};

const AllEntitiesStateHandler = () => {
    const user = useGetUser();

    if (user && !user.hasInitialSetupCompleted) {
        return (
            <NotConfiguredEmptyState />
        );
    }

    return <AllEntities />;
};

const messages = defineMessages({
    addNew: {
        id: 'src.Screens.Dashboard.AllEntities.AllEntities.addNew',
        defaultMessage: 'Dodaj nową',
    },
});

export default AllEntitiesStateHandler;
